import { MachineCategoryPageModelType } from '@internal/page/TMachineCategoryPageData'
import { KnownBaleChamberType } from '@internal/utils/machine/KnownBalerChamberType'
import { KnownMachineHeaderType } from '@internal/utils/machine/KnownMachineHeaderType'
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute'
import { MachineType } from '@internal/utils/machine/MachineType'
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName'
import {
  matchMachineCategoryPage,
  matchSearchPage,
} from '@internal/utils/routing/router'
import { urlFor } from '@internal/utils/routing/urls/urlFor'
import { TFilterableBaleChamberOption } from '@internal/utils/search/TFilterableBalerChamberOption'
import { TFilterableHeaderTypeOption } from '@internal/utils/search/TFilterableHeaderTypeOption'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { usePageContext } from 'page/PageContext'
import { ParsedUrlQuery } from 'querystring'
import { useEffect, useMemo, useState } from 'react'
import { PageTypeKey } from 'utils/page/PageTypeKey'
import {
  isValidBaleChamberOption,
  isValidHeaderTypeOption,
} from '../../utils/search/isValidBaleChamberOption'
import { SearchResultsSortKey } from './SearchResultsSortKey'
import { TRangeFiltersMap } from './TRangeFiltersMap'
import { mappedModelSuffix, SearchModel, TSearchParams } from './TSearchParams'
import { getCleanMachineSubType } from './getCleanMachineType'
import { isValidSortValue } from './isValidSortValue'
import { loadChunkSize } from './loadChunkSize'

const queryParamToMachineAttribute = (param: string) =>
  param.toUpperCase().replace(/-/g, '_') as MachineAttribute

const toArray = (items: string | string[] | undefined): string[] =>
  items ? (Array.isArray(items) ? items : [items]) : []

export const getLocations = (items: string | string[] | undefined): string[] =>
  toArray(items).map((location) => location.toUpperCase())

export const getFeatures = (items: string | string[] | undefined) =>
  toArray(items).map(queryParamToMachineAttribute)

export const getBaleChamber = (option: string): TFilterableBaleChamberOption =>
  isValidBaleChamberOption(option) ? option : KnownBaleChamberType.FIXED_CHAMBER

export const getHeaderType = (option: string): TFilterableHeaderTypeOption =>
  isValidHeaderTypeOption(option) ? option : KnownMachineHeaderType.HEADER

export const getSort = (item: string | string[] | undefined) => {
  const sort = `${item}` as SearchResultsSortKey
  return isValidSortValue(sort) ? sort : SearchResultsSortKey.RelevanceAsc
}

export const getRangeFiltersMap = (queryParams: ParsedUrlQuery) => {
  return Object.keys(queryParams).reduce<TRangeFiltersMap>((map, key) => {
    const attribute = queryParamToMachineAttribute(key)
    const value = (queryParams[key] ?? '').toString()

    const values = value.replace('x', '').split('-')
    const minValue = parseInt(values[0] ?? '')
    const maxValue = parseInt(values[1] ?? '')
    const min = isNaN(minValue) ? undefined : minValue
    const max = isNaN(maxValue) ? undefined : maxValue

    if (min || max) {
      map[attribute] = {
        min,
        max,
      }
    }
    return map
  }, {})
}

type SearchParamsResult = TSearchParams & {
  pathMachineType: MachineType | null
  pageMachineType: MachineType | null
}

export const useSearchParams = () => {
  const { locale, pageData, machineType: pageMachineType } = usePageContext()
  const {
    query: {
      brand: queryBrand,
      model: queryModel,
      baseModel: queryBaseModel,
      modelSeries: queryModelSeries,
      machineSubType: queryMachineSubType,
      headerType: queryHeaderType,
      baleChamber: queryBaleChamber,
      amount: queryAmount,
      feature: queryFeature,
      location: queryLocation,
      sort: querySort,
      ...queryRangeFilters
    },
    asPath,
  } = useRouter()

  const { getMachineTypeForMachineTypeSlug } = useMachineTypeName()

  const pathMachineType = useMemo(() => {
    // as path includes the query params, we need to remove them to get the correct url and prepend the locale
    const url = urlFor(locale, asPath.split('?')[0])
    const routeMatchResult = matchSearchPage(locale, url, {})
    const machineCategoryPageMatchResult = !routeMatchResult
      ? matchMachineCategoryPage(locale, url, {})
      : null

    const matchResult = routeMatchResult
      ? routeMatchResult.pathParams
      : machineCategoryPageMatchResult
        ? machineCategoryPageMatchResult.params.pathParams
        : null

    const pathMachineTypeSlug =
      matchResult &&
      'machineTypeSlug' in matchResult &&
      matchResult.machineTypeSlug
        ? (matchResult.machineTypeSlug as string)
        : null

    const pathMachineType = pathMachineTypeSlug
      ? getMachineTypeForMachineTypeSlug(pathMachineTypeSlug)
      : null

    return pathMachineType
  }, [locale, asPath, getMachineTypeForMachineTypeSlug])

  const machineType = pathMachineType

  const machineCategoryPageParams = useMemo(() => {
    if (!pageData || pageData.type !== PageTypeKey.MachineCategoryPage) {
      return null
    }

    const data: {
      machineType: MachineType
      model?: SearchModel | null
      brand?: string | null
    } = {
      machineType: pageData.machineType,
    }

    if (pageData.brand) {
      data.brand = pageData.brand
    }

    if (pageData.model) {
      data.model = {
        name: pageData.model,
        type:
          pageData.modelType === MachineCategoryPageModelType.Model
            ? 'mapped'
            : pageData.modelType === MachineCategoryPageModelType.BaseModel
              ? 'base'
              : 'fuzzy',
      }
    }
    return data
  }, [pageData])
  const parsedAmount = parseInt(`${queryAmount}`)
  const amount = isNaN(parsedAmount) ? loadChunkSize : parsedAmount

  // BRANDS
  const [brands, setBrands] = useState<string[]>(
    toArray(queryBrand ?? machineCategoryPageParams?.brand ?? undefined)
  )

  useEffect(() => {
    const newBrands = toArray(
      queryBrand ?? machineCategoryPageParams?.brand ?? undefined
    )
    if (!isEqual(newBrands, brands)) {
      setBrands(newBrands)
    }
  }, [brands, queryBrand, pageData, machineCategoryPageParams?.brand])

  // LOCATIONS
  const [locations, setLocations] = useState<string[]>(
    getLocations(queryLocation)
  )
  useEffect(() => {
    const newLocations = getLocations(queryLocation)
    if (!isEqual(newLocations, locations)) {
      setLocations(newLocations)
    }
  }, [locations, queryLocation])

  // FEATURES
  const [features, setFeatures] = useState<MachineAttribute[]>(
    getFeatures(queryFeature)
  )
  useEffect(() => {
    const newFeatures = getFeatures(queryFeature)
    if (!isEqual(newFeatures, features)) {
      setFeatures(newFeatures)
    }
  }, [features, queryFeature])

  // RANGE FILTERS
  const [rangeFilters, setRangeFilters] = useState<TRangeFiltersMap>(
    getRangeFiltersMap(queryRangeFilters)
  )

  // BALE_CHAMBER
  const baleChamber = queryBaleChamber
    ? getBaleChamber(queryBaleChamber?.toString())
    : undefined

  useEffect(() => {
    const newRangeFilters = getRangeFiltersMap(queryRangeFilters)
    if (!isEqual(newRangeFilters, rangeFilters)) {
      setRangeFilters(newRangeFilters)
    }
  }, [queryRangeFilters, rangeFilters])

  // HEADER_TYPE
  const headerType = queryHeaderType
    ? getHeaderType(queryHeaderType?.toString())
    : undefined

  const params = useMemo<SearchParamsResult>(() => {
    const machineSubType = machineType
      ? getCleanMachineSubType(queryMachineSubType?.toString(), machineType)
      : null

    let models: SearchModel[] = []

    if (typeof queryModel === 'string' && queryModel.length > 0) {
      models.push({
        name: queryModel.replace(mappedModelSuffix, ''),
        type: queryModel.endsWith(mappedModelSuffix) ? 'mapped' : 'fuzzy',
      })
    } else if (Array.isArray(queryModel)) {
      models.push(
        ...queryModel.map(
          (name): SearchModel => ({
            name: name.replace(mappedModelSuffix, ''),
            type: name.endsWith(mappedModelSuffix) ? 'mapped' : 'fuzzy',
          })
        )
      )
    }

    if (typeof queryBaseModel === 'string' && queryBaseModel.length > 0) {
      models.push({
        name: queryBaseModel,
        type: 'base',
      })
    } else if (Array.isArray(queryBaseModel)) {
      models.push(
        ...queryBaseModel.map(
          (name): SearchModel => ({
            name,
            type: 'base',
          })
        )
      )
    }

    if (typeof queryModelSeries === 'string' && queryModelSeries.length > 0) {
      models.push({
        name: queryModelSeries,
        type: 'series',
      })
    } else if (Array.isArray(queryModelSeries)) {
      models.push(
        ...queryModelSeries.map(
          (name): SearchModel => ({
            name,
            type: 'series',
          })
        )
      )
    }

    if (models.length === 0 && machineCategoryPageParams?.model) {
      models.push(machineCategoryPageParams.model)
    }

    const sort = getSort(querySort)

    const params: SearchParamsResult = {
      models,
      brands,
      machineType: machineType ?? undefined,
      machineSubType: machineSubType ?? undefined,
      headerType,
      baleChamber,
      amount,
      features,
      rangeFilters,
      locations,
      sort,
      pathMachineType,
      pageMachineType,
    }

    return params
  }, [
    machineType,
    queryMachineSubType,
    queryModel,
    queryBaseModel,
    queryModelSeries,
    machineCategoryPageParams?.model,
    querySort,
    brands,
    headerType,
    baleChamber,
    amount,
    features,
    rangeFilters,
    locations,
    pathMachineType,
    pageMachineType,
  ])

  return params
}

export const getSearchParamsCount = (searchParams: TSearchParams) => {
  return (
    searchParams.brands.length +
    searchParams.models.length +
    (searchParams.machineType ? 1 : 0) +
    (searchParams.machineSubType ? 1 : 0) +
    (searchParams.headerType ? 1 : 0) +
    (searchParams.baleChamber ? 1 : 0) +
    searchParams.features.length +
    Object.keys(searchParams.rangeFilters).length +
    searchParams.locations.length
  )
}

export const getHasAnySearchParams = (searchParams: TSearchParams) =>
  getSearchParamsCount(searchParams) > 0
